import styled, { keyframes } from 'styled-components';
import { BodyCell } from '../../../components_v2/table/style/Style';
import { DefaultImage } from '../../../styles/global/css/GlobalImage';
import { DefaultEditable, DefaultTextBlack, TitleFontFamily } from '../../../styles/global/css/GlobalText';
import { PrimaryColor, BorderColor, LightGreySidely3, RedSidely } from '../../../styles/global/css/Utils';
import { TableRowStatus } from '../../orders/templateOrders/style/Style';

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
`;

interface LeftContainerProps {
	isFullOpen: boolean
}

const LeftContainer = styled.div<LeftContainerProps>`
    transition: width 0.4s;
    width: ${p => p.isFullOpen ? '25%' : '100%'};
    ${p => p.isFullOpen ? `border-right: 1px solid ${BorderColor}; padding-right: 10px;` : ''}
    display: flex;
    flex-flow: column;
    height: 100%;
`;

interface MiddleContainerProps {
	isFullOpen: boolean
	isOpen: boolean
}

const MiddleContainer = styled.div<MiddleContainerProps>`
    transition: width 0.4s;
    display: ${p => p.isFullOpen ? 'initial' : 'none'};
    width: ${p => p.isOpen ? '45%' : '0'};
    border-right: 1px solid ${BorderColor};
`;

interface RightContainerProps {
	isFullOpen: boolean
	isOpen: boolean
}

const RightContainer = styled.div<RightContainerProps>`
    transition: width 0.4s;
    display: ${p => p.isFullOpen ? 'flex' : 'none'};
    width: ${p => p.isOpen ? '30%' : '75%'};
    flex-direction: column;
    align-items: center;
    height: 100%;
`;

const Header = styled.div<{ minHeight?: string }>`
    display: flex;
    width: 100%;
    height: 11%;
    border-bottom: 1px solid ${BorderColor};
    min-height: ${p => p.minHeight ?? '120px'};
    justify-content: space-between;
`;

const HeaderLeft = styled.div<{ width?: string }>`
    display: flex;
    ${p => p.width ? `
        width: ${p.width};
    ` : ''};
`;

const HeaderRight = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 6px;
    margin-right: 2px;
`;

interface FullOpenProps {
	isActive: boolean
}

const FullOpen = styled(DefaultImage) <FullOpenProps>`
    cursor: pointer;
    transform: rotate(${p => p.isActive ? '0' : '180'}deg);
    transition: 0.5s;
    width: 18px;
`;

interface HeaderBlockProps {
	flexDirection: string
	marginLeft?: string
	alignItem?: string
	padding?: string
	width?: string
	error?: boolean
	gap?: string
}

const HeaderBlock = styled.div<HeaderBlockProps>`
    display: flex;
    flex-direction: ${p => p.flexDirection};
    margin-left: ${p => p.marginLeft ?? 'initial'};
    align-items: ${p => p.alignItem ?? 'center'};
    ${p => p.padding ? `padding: ${p.padding};` : ''}
    width: ${p => p.width ?? '100%'};
    ${p => p.error
		? `
        background-color: color-mix(in oklab, ${RedSidely} 15%, transparent);
        color: ${RedSidely};
        `
		: ''}
    ${({ gap }) => gap ? `gap: ${gap};` : ''}
`;

interface LogoProps {
	src: string
}

const Logo = styled.div<LogoProps>`
    width: 40px;
    height: 40px;
    padding: 5px;
    border-radius: 50%;
    background-image: url('${p => p.src}');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    flex-shrink: 0;
`;

const LogoJsx = styled.div`
    width: 40px;
    height: 40px;
    padding: 5px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${LightGreySidely3};
    flex-shrink: 0;
`;

const Body = styled.div<{ overflow?: string, columns?: boolean }>`
    display: flex;
    height: calc(100vh * 0.9 - 45px);
    ${p => p.overflow ? `overflow: ${p.overflow};` : ''}
    ${p => p.columns ? 'flex-flow: column;' : ''}
`;

interface BodyBlockProps {
	flexDirection: string
	isOpen: boolean
	marginLeft?: string
	alignItems?: string
}

const BodyBlock = styled.div<BodyBlockProps>`
    display: ${p => p.isOpen ? 'flex' : 'none'};
    flex-direction: ${p => p.flexDirection};
    margin-left: ${p => p.marginLeft ?? 'initial'};
    width: 85%;
    ${p => p.alignItems ? `align-items: ${p.alignItems}` : ''};
`;

const BodyHeaderStatus = styled(TableRowStatus) <{ inDropdown?: boolean }>`
    ${p => p.inDropdown
		? `
        margin: 5px;
        cursor: pointer;
        text-align: center;
        width: fit-content;
    `
		: ''}
`;

const BodyMainContainer = styled.div<{ noScroll?: boolean, noHeight?: boolean, width?: string }>`
    width: ${p => p.width ?? '85%'};
    ${p => p.noHeight ? '' : 'height: calc(100vh - 160px);'}
    ${p => p.noScroll ? '' : 'overflow-y: auto'};
`;

const BodyMainRow = styled.div<{ gap?: string }>`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    align-content: center;
    padding-top: 20px;
    ${({ gap }) => gap ? `gap: ${gap};` : ''}
`;

const TagWrapper = styled.div<{ isOpen?: boolean, horizontal?: boolean, center?: boolean, noOverflow?: boolean }>`
    ${p => p.horizontal
		? `
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        ${p.center ? 'justify-content: center;' : ''}
        overflow-x: auto;
        width: 100%;
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
        &::-webkit-scrollbar {
          display: none;
        }
        align-items: center;
    `
		: `
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        height: ${p.isOpen ? 'fit-content' : '37px'};
        overflow: ${p.noOverflow ? 'visible' : 'hidden'};
        ${p.center ? 'justify-content: center;' : ''}
        width: 100%;
        align-items: center;
    `}
`;

const ShakingAnimation = keyframes`
    0% { transform: rotate(0deg); }
    25% { transform: rotate(10deg); }
    50% { transform: rotate(0eg); }
    75% { transform: rotate(-10deg); }
    100% { transform: rotate(0deg); }
`;

interface BodyMainImageProps {
	src: string
	backgroundSize?: string
	noBorder?: boolean
	size?: string
	opacity?: string
}

const BodyMainImage = styled.div<BodyMainImageProps>`
    width: ${p => p.size ?? '20px'};
    height: ${p => p.size ?? '20px'};
    background-image: url(${p => p.src});
    background-size: ${p => p.backgroundSize ?? 'cover'};
    background-repeat: no-repeat;
    background-position: center;
    border-radius: ${p => p.noBorder ? '0' : '50%'};
    ${p => p.opacity ? `opacity: ${p.opacity};` : ''}
    flex-shrink:0;

    &:hover{
        // animation: ${ShakingAnimation} 0.4s;
    }
`;

interface BodyMainTextProps {
	marginLeft?: string
	cursor?: string
	isLink?: boolean
	error?: boolean
	maxWidth?: string
	display?: string
	noOverflow?: boolean
	fontSize?: string
	fontWeight?: string
	color?: string
	width?: string
	minWidth?: string
	minHeight?: string
	height?: string
}

const BodyMainText = styled.p<BodyMainTextProps>`
    ${DefaultTextBlack}
    transition: all 0.4s ease;
height: ${p => p.height ?? '100%'};
    margin-left: ${p => p.marginLeft ?? '20px'};
    cursor: ${p => p.cursor ?? 'auto'};
    display: ${p => p.display ?? 'flex'};
    align-items: center;
    min-width: ${p => p.minWidth ?? '50px'};
    ${p => p.maxWidth ? `max-width: ${p.maxWidth};` : ''}
    ${p => p.width ? `width: ${p.width};` : ''}
    ${p => p.color ? `color: ${p.color};` : ''}
    ${p => p.fontSize ? `font-size: ${p.fontSize};` : ''}
    ${p => p.fontWeight ? `font-weight: ${p.fontWeight};` : ''}
    ${p => p.minHeight ? `min-height: ${p.minHeight};` : 'min-height: 25px;'}


    ${p => p.noOverflow ? '' : `
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow-x: hidden;
    `}

    &:hover {
        ${p => p.isLink ? `color: ${PrimaryColor}` : ''};
    }

    &:empty {
        &:not(:focus) {
            &:before {
                content: '-';
            }
        }
    }
`;

const BodyMainTextEditable = styled(BodyMainText) <{ whiteSpace?: string }>`
    ${DefaultEditable}
    font-family: ${TitleFontFamily};
    cursor: ${p => p.cursor ?? 'pointer'};
    overflow-y:hidden;
    white-space: ${({ whiteSpace }) => whiteSpace ?? 'break-spaces'};
    word-break:break-word;
    border-bottom: 1px solid transparent;
    &:hover {
        border-color: ${p => p.error ? RedSidely : PrimaryColor};
    }
    ${p => p.error
		? `
        &:focus-visible {
            background-color: color-mix(in oklab, ${RedSidely} 15%, transparent);
        }
        color: ${RedSidely};
        `
		: ''}
`;

const PenEdition = styled(DefaultImage) <{ parent?, hided?: boolean }>`
    transition: opacity 0.4s ease;
    width: 20px;
    height: 20px;
    opacity: 0;
    display: ${p => p.hided ? 'block' : 'none'};
    margin-left: 5px;

    ${BodyMainRow}:hover & {
        opacity: 0.6;
        display:block;
    }

    ${HeaderBlock}:hover & {
        opacity: 0.6;
        display:block;
    }

    ${BodyCell}:hover & {
        opacity: 0.6;
        display:block;
    }

    ${p => p.parent}:hover & {
        opacity: 0.6;
        display:block;
    }
`;

interface ButtonActionProps {
	width?: string
}

const ButtonAction = styled(PenEdition) <ButtonActionProps>`
    cursor: pointer;
    width: ${p => p.width ?? '25px'};
    height: ${p => p.width ?? '25px'};
`;

interface BodyMainPointProps {
	color: string
}

const ContainerPoint = styled.div<BodyMainPointProps>`
    background-color: ${p => p.color};
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    padding: 6px;
    width: 30px;
    min-width: 30px;
    height: 30px;
`;

const BodyMainPoint = styled(DefaultImage)`
    padding: 10px;
    width: 50px;
    height: 50px;
    filter: brightness(100);
`;

const BodyMainLoaderContainer = styled.div`
    height: 30px;
`;

export {
	Body,
	BodyBlock, BodyHeaderStatus,
	BodyMainContainer, BodyMainImage,
	BodyMainLoaderContainer, BodyMainPoint, BodyMainRow, BodyMainText,
	BodyMainTextEditable, ButtonAction, Container, ContainerPoint, FullOpen, Header, HeaderBlock, HeaderLeft,
	HeaderRight, LeftContainer, Logo, LogoJsx, MiddleContainer, RightContainer,
	TagWrapper
};
